import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  ArrowPathIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  MapPinIcon,
  ClockIcon,
  PlusIcon,
  PencilIcon
} from '@heroicons/react/24/solid';
import type { Event } from '../../types/charity';
import { api } from '../../services/api';
import { useDevMode } from '../../utils/environment';

export default function Events() {
  const isDevEnvironment = useDevMode();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    fetchEvents();
  }, []);
  
  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await api.get<Event[]>('/charity/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status: Event['status']) => {
    switch (status) {
      case 'upcoming':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <ClockIcon className="mr-1 h-3 w-3" />
            Upcoming
          </span>
        );
      case 'ongoing':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CalendarDaysIcon className="mr-1 h-3 w-3" />
            In Progress
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <CalendarDaysIcon className="mr-1 h-3 w-3" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <CalendarDaysIcon className="mr-1 h-3 w-3" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };
  
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  const getAttendeeStatus = (currentAttendees: number, maxAttendees: number | null) => {
    if (!maxAttendees) return null;
    
    const percentage = (currentAttendees / maxAttendees) * 100;
    
    if (percentage >= 90) {
      return (
        <div className="mt-1">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-red-500 h-2 rounded-full" style={{ width: `${percentage}%` }}></div>
          </div>
          <p className="text-xs text-red-600 mt-1">Almost full! {currentAttendees}/{maxAttendees} spots taken</p>
        </div>
      );
    } else if (percentage >= 60) {
      return (
        <div className="mt-1">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${percentage}%` }}></div>
          </div>
          <p className="text-xs text-yellow-600 mt-1">Filling up: {currentAttendees}/{maxAttendees} spots taken</p>
        </div>
      );
    } else {
      return (
        <div className="mt-1">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-green-500 h-2 rounded-full" style={{ width: `${percentage}%` }}></div>
          </div>
          <p className="text-xs text-green-600 mt-1">{currentAttendees}/{maxAttendees} spots taken</p>
        </div>
      );
    }
  };
  
  const filteredEvents = events.filter(event => {
    // Apply search filter
    if (searchTerm && !event.title.toLowerCase().includes(searchTerm.toLowerCase()) && !event.location.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Apply status filter
    if (statusFilter !== 'all' && event.status !== statusFilter) {
      return false;
    }
    
    return true;
  });
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-xl font-medium text-navy-blue">Events</h1>
            </div>
            <div className="space-y-2">
              {/* Filters and Actions */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {events.length}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search events..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-sky-blue focus:border-sky-blue sm:text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <select
                  className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-sky-blue focus:border-sky-blue sm:text-sm rounded-md"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
                  onClick={fetchEvents}
                >
                  <ArrowPathIcon className="h-5 w-5 mr-2" />
                  Refresh
                </button>
                <Link
                  to={isDevEnvironment ? "/dev/charity/events/create" : "/charity/events/create"}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-navy-blue hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create Event
                </Link>
              </div>
            </div>
            
            {/* Events Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                <div className="col-span-full text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-blue mx-auto"></div>
                  <p className="mt-4 text-gray-500">Loading events...</p>
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500">No events found</p>
                </div>
              ) : (
                filteredEvents.map((event) => (
                  <div key={event.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    {event.image_url && (
                      <div className="aspect-w-16 aspect-h-9">
                        <img
                          src={event.image_url}
                          alt={event.title}
                          className="object-cover w-full h-48"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex justify-between items-start">
                        <h2 className="text-lg font-medium text-navy-blue">{event.title}</h2>
                        {getStatusBadge(event.status)}
                      </div>
                      <p className="mt-2 text-sm text-gray-500 line-clamp-2">{event.description}</p>
                      <div className="mt-4 space-y-2">
                        <div className="flex items-center text-sm text-gray-600">
                          <CalendarDaysIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {formatDate(event.start_date)}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPinIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {event.location}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <UserGroupIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {event.current_attendees} {event.max_attendees ? `of ${event.max_attendees}` : ''} attendees
                        </div>
                        {getAttendeeStatus(event.current_attendees, event.max_attendees)}
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Link
                          to={`/events/${event.id}`}
                          className="inline-flex items-center text-sm font-medium text-sky-blue hover:text-navy-blue"
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          Edit Event
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
